export type View = 'river_oaks' | 'highland_park' | 'ga' | 'consolidated';

export const LOCATION_VIEWS: readonly View[] = ['river_oaks', 'highland_park', 'ga'];

export type DriverType =
    | 'count_value'
    | 'count_per_calendar_day'
    | 'count_per_open_day'
    | 'dollar_value'
    | 'dollar_per_calendar_day'
    | 'dollar_per_pay_period'
    | 'dollar_per_open_day'
    | 'dollar_per_sq_ft'
    | 'dollar_per_1000_sqft_calendar_day'
    | 'dollar_per_fte'
    | 'mom_pct'
    | 'mom_dollar'
    | 'yoy_pct'
    | 'yoy_dollar'
    | 'pct_gross_sales'
    | 'pct_gross_sales_growth'
    | 'pct_of_line'
    | 'dollar_per_line_unit'
    | 's_curve'
    | 'dio'
    | 'dso'
    | 'dpo'
    | 'orders_x_ticket'
    | 'linked'
    | 'plug'
    | 'carry_forward'
    | 'ignore'
    | 'labor_buildup';

export const FIXED_DRIVERS: readonly DriverType[] = [
    'orders_x_ticket',
    'linked',
    'plug',
    'carry_forward',
];

export type Formula =
    | { op: 'sum'; of: (string | number | Formula)[] }
    | { op: 'sub'; a: string | number | Formula; b: string | number | Formula }
    | { op: 'div'; a: string | number | Formula; b: string | number | Formula }
    | { op: 'mul'; of: (string | number | Formula)[] };

export interface SchemaEntry {
    key: string;
    tab: 'IS' | 'BS';
    row: number | null;
    label: string;
    indent: number;
    kind: 'input' | 'subtotal' | 'metric' | 'header' | 'check' | 'text';
    unit: 'usd' | 'count' | 'pct' | 'text';
    sign: 1 | -1;
    formula: Formula | null;
    italic: boolean;
    drivers: string[];
    bs_only: boolean;
    linkage: { source: string } | null;
    computed?: boolean;
    fixed_label?: string;
}

export type PayPeriodCadence = 'biweekly' | 'semimonthly' | 'weekly' | 'monthly';

export interface DriverParams {
    ref?: string;
    start?: number;
    end?: number;
    midpoint?: number;
    steepness?: number;
    cadence?: PayPeriodCadence;
    anchor_date?: string;
    net_of_benefits_pct?: number;
}

export interface LaborMeta {
    residual: Record<View, Record<string, string | null>>;
    salaried_shares: Record<View, { boh: string | null; foh: string | null }>;
    first_month: string;
}

export interface RecipeDefaults {
    default_pay_cadence?: PayPeriodCadence;
    default_pay_anchor_date?: string | null;
}

export type RecipeAnchorBasis =
    | 'manual'
    | 'last_actual'
    | 'trailing_avg'
    | 'last_actual_per_period'
    | 'trailing_avg_per_period';

export interface RecipeAnchor {
    value: number;
    basis?: RecipeAnchorBasis;
    months?: 1 | 3 | 6 | 12;
    runs?: 1 | 3 | 6;
    history_value?: number;
}

export interface RecipePathSegment {
    to: number;
    by: string;
    shape?: 'linear' | 'step';
}

export interface RecipeGrowthSteps {
    months: number[];
    per_year: number;
}

export interface RecipeGrowth {
    per_month?: number | Record<string, number>;
    per_year?: number | Record<string, number>;
    steps?: RecipeGrowthSteps;
}

export interface RecipeSeasonalityFromHistory {
    years: number;
    amplitude?: number;
    source_line?: string;
}

export interface RecipeSeasonality {
    factors?: Record<string, number>;
    from_history?: RecipeSeasonalityFromHistory;
}

export interface RecipeOverride {
    value: number;
    reason?: string | null;
    edited_by?: string | null;
    edited_at?: string | null;
}

export interface Recipe {
    anchor: RecipeAnchor;
    path?: RecipePathSegment[];
    growth?: RecipeGrowth;
    seasonality?: RecipeSeasonality;
    overrides?: Record<string, number | RecipeOverride>;
    rationale?: string;
}

export interface DriverTypeInput {
    values: (number | null)[];
    params?: DriverParams;
    recipe?: Recipe | null;
}

export interface LineDriverState {
    active: DriverType | null;
    by_type: Record<string, DriverTypeInput>;
}

export interface OneTimeItem {
    id: string;
    month: string;
    view: View;
    line_key: string;
    amount: number;
    mode: 'incremental' | 'already_in_driver';
    notes?: string;
}

export type CashPolicyCadence = 'biweekly' | 'monthly';

export type CashPolicyYieldView = 'river_oaks' | 'highland_park' | 'ga';

export type WaterfallMode = 'sheet' | 'practice' | 'no_trueups';

export interface CashPolicyOneTimeDistribution {
    date: string;
    amount: number;
}

export interface CashPolicySplitScheduleBasis {
    anchor_date: string;
    cadence: CashPolicyCadence;
    amount_per_period: number;
    horizon_months: number;
    last_dist_num: number;
    last_closed_month: string;
    mode: WaterfallMode;
    one_time_date?: string | null;
    one_time_amount?: number | null;
}

export interface CashPolicySplitSchedulePeriod {
    date: string;
    t: number;
    tiers: Record<string, number>;
}

export interface CashPolicySplitScheduleMonth {
    t: number;
    lines: Record<string, number>;
}

export interface CashPolicySplitSchedule {
    basis: CashPolicySplitScheduleBasis;
    computed_at: string;
    months: CashPolicySplitScheduleMonth[];
    periods: CashPolicySplitSchedulePeriod[];
    pref_accrual?: CashPolicySplitScheduleMonth[];
}

export interface CashPolicy {
    enabled: boolean;
    floor: {
        operating_minimum: number | null;
        drawdown_allowance: number | null;
    };
    distribution: {
        cadence: CashPolicyCadence;
        anchor_date: string;
        amount_per_period: number | null;
        one_time?: CashPolicyOneTimeDistribution | null;
        split_schedule: CashPolicySplitSchedule | null;
    };
    sweep: {
        enabled: boolean;
        mode: 'excess' | 'fixed';
        amount_per_period: number | null;
        yield_snapshot: {
            as_of: string;
            source: string;
            spread: number;
            apr_by_month: number[];
        } | null;
        yield_income_view: CashPolicyYieldView | null;
        opening_mm_balance: number | null;
    };
    solver: {
        horizon_months: 12 | 24 | 36;
        round_to: 500 | 1000 | 5000;
    };
}

export interface CashPolicyMonthResult {
    t: number;
    month: string;
    n_distributions: number;
    pre_policy_cash: number;
    generation: number;
    distributions: number;
    logged_distributions: number;
    policy_distributions: number;
    one_time_distributions: number;
    initial_transfer: number;
    sweep: number;
    interest: number;
    apr: number;
    operating_cash: number;
    mm_balance: number;
    total_cash: number;
    breach: boolean;
    shortfall: number;
    split_source: 'waterfall' | 'tier4_placeholder';
}

export interface CashPolicySummary {
    min_operating_cash: number;
    min_operating_cash_t: number;
    breach_months: number[];
    max_shortfall: number;
    distributions_12m: number;
    sweep_12m: number;
    interest_12m: number;
    mm_balance_t12: number;
    mm_balance_t24: number;
    mm_balance_t36: number;
    split_source: 'waterfall' | 'tier4_placeholder';
}

export interface CashPolicyResult {
    effective_floor: number;
    months: CashPolicyMonthResult[];
    split_source: 'waterfall' | 'tier4_placeholder';
    summary: CashPolicySummary;
}

export interface LoggedDistributionRow {
    num: number;
    date: string;
    amount: number;
    lines: Record<string, number> | null;
}

export interface EvaluateContext {
    tier4_shares?: Record<string, number>;
    last_dist_num?: number | null;
    last_dist_date: string | null;
    logged_after_close: LoggedDistributionRow[];
    waterfall_mode: string | null;
    last_closed_month: string;
    excess_sweep_by_t?: Record<number, number>;
}

export interface CaseInputs {
    consolidated_only: string[];
    drivers: Record<string, Record<string, LineDriverState>>;
    one_time_items: OneTimeItem[];
    cash_policy: CashPolicy | null;
}

export interface History {
    nHistory: number;
    lastClosedMonth: string;
    values: Record<View, Record<string, (number | string | null)[]>>;
    laborMeta?: LaborMeta | null;
}

export interface Diagnostic {
    code: string;
    message: string;
    view?: View;
    lineKey?: string;
    t?: number;
}

export interface Model {
    nHistory: number;
    values: Record<View, Record<string, Record<number, number | string | null>>>;
    kpis: {
        net_income: { t3: number | null; t6: number | null; t12: number | null };
        cash: { t3: number | null; t6: number | null; t12: number | null };
        total_cash: { t3: number | null; t6: number | null; t12: number | null };
    };
    cash_policy: CashPolicyResult | null;
    diagnostics: Diagnostic[];
}

export type ValidationSection =
    | 'Operational Data'
    | 'Income Statement'
    | 'Balance Sheet'
    | 'Cash Flow'
    | 'Cash policy';

export interface ValidationIssue {
    code: string;
    line_key: string;
    view: View;
    t: number | null;
    message: string;
    label: string;
    section: ValidationSection;
}

export interface ValidationThresholds {
    outlier_multiple: number;
    outlier_abs: number;
    mom_pct_max: number;
    yoy_pct_max: number;
    days_max: number;
}

export interface ValidationOptions {
    caseName?: string;
    otherNames?: string[];
    expiresOn?: string;
    today?: string;
    lastDistNum?: number | null;
    lastDistDate?: string | null;
    lastClosedMonth?: string | null;
    waterfallMode?: string | null;
    solverMax?: number;
    skipRecipeMismatch?: boolean;
}

export interface ValidationResult {
    errors: ValidationIssue[];
    warnings: ValidationIssue[];
}

export const VIEW_TO_TAB: Record<Exclude<View, 'consolidated'>, string> = {
    river_oaks: 'IS_RIVEROAKS',
    highland_park: 'IS_HIGHLANDPARK',
    ga: 'IS_GA',
};

export const BS_TAB = 'BS_FULL';

export const VALUE_COL_OFFSET = 7;

export const FORCED_IS_KEYS = ['is.net_product_sales', 'is.gross_product_sales'] as const;
